import { assertEvent, assign } from 'xstate5';

import { getElementConfig, getIntentConfig } from '@order/integrations/stripe';

import { sumLedgerTotal } from '../components';
import { OrderingPaymentActions } from './ordering-machine.actions.payment';
import { OrderingPostOrderActions } from './ordering-machine.actions.post';
import { OrderingRewardsActions } from './ordering-machine.actions.rewards';
import { OrderingTimeActions } from './ordering-machine.actions.time';
import { type OrderingMachineAction } from './ordering-machine.actions.types';
import { initialContext } from './ordering-machine.context';
import { orderingMachineLog } from './ordering-machine.logger';
import { getNewTip } from './ordering-machine.utils';

// ─────────────────────────────────────────────────────────────────────────────

const resetContext: OrderingMachineAction = assign(({ context }) => {
  return {
    ...initialContext,
    orderSubmissionDelay: context.orderSubmissionDelay,
    wantedTimesPollingInterval: context.wantedTimesPollingInterval,
    isPaymentStatePollingEnabled: context.isPaymentStatePollingEnabled,
    paymentStatePollingInterval: context.paymentStatePollingInterval,
    paymentStateExpirationTolerance: context.paymentStateExpirationTolerance,
    paymentStateDelayedTolerance: context.paymentStateDelayedTolerance,
    stripeConfirmHandlerRef: context.stripeConfirmHandlerRef,
    confirmStripe: context.confirmStripe,
  };
});

const onFetchInitialDataDone: OrderingMachineAction = assign({
  isWantedTimeAsap: true,
  orderId({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.cart.orderId;
  },
  cart({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.cart;
  },
  paymentMethods({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.paymentMethods;
  },
  defaultPaymentMethodId({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.defaultPaymentMethodId;
  },
  hasDefaultPaymentMethodCard({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.hasDefaultPaymentMethodCard;
  },
  selectedTip({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.selectedTip;
  },
  availableTips({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.availableTips;
  },
  availablePoints({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.availablePoints;
  },
  availableCredit({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.availableCredit;
  },
  giftCardBalance({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.giftCardBalance;
  },
  customer({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    const o = event.output;
    return {
      customerId: o.customerId,
      firstName: o.firstName,
      lastName: o.lastName,
      email: o.email,
      phoneNumber: o.phoneNumber,
      isNewCustomer: o.isNewCustomer,
      isOptedOutFromLoyalty: o.isOptedOutFromLoyalty,
    };
  },
  wantedTime({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.cart.wantedTimes[0];
  },
  dropoffLocation({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.cart.dropoffLocations[0]?.value;
  },
  appliedRewardId({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.cart.appliedRewardId;
  },
  stripeCustomerId({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.stripeCustomerId;
  },
  stripeCustomerKey({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.stripeCustomerKey;
  },
  stripeSession({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    return event.output.stripeSession;
  },
  stripeIntentConfig({ context, event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');
    const { cart, selectedTip, availableCredit } = event.output;

    const { stripeConfirmHandlerRef, isPaymentStatePollingEnabled } = context;
    const { total } = sumLedgerTotal(cart.ledger, selectedTip, availableCredit, true); // prettier-ignore
    const amount = total <= 0 ? 100 : total;

    return getIntentConfig({
      isPaymentStatePollingEnabled,
      amount,
      stripeConfirmHandlerRef,
    });
  },
  stripeElementConfig({ event }) {
    assertEvent(event, 'xstate.done.actor.fetchInitialData');

    return getElementConfig(event.output);
  },
});

const setUpsellBeingAdded: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated({ context, event }) {
    assertEvent(event, 'ADD_UPSELL_TO_BAG');
    return [...context.lineItemIdsBeingUpdated, event.params.productId];
  },
});

const setLineItemQuantity: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated({ context, event }) {
    assertEvent(event, 'CHANGE_LINE_ITEM_QUANTITY');
    return [...context.lineItemIdsBeingUpdated, event.params.lineItemId];
  },
});

const onAddUpsellToBagDone: OrderingMachineAction = assign({
  lineItemIdsBeingUpdated: [],
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.addUpsellToBag');
    if ('cart' in event.output) return event.output.cart ?? context.cart;
    return context.cart;
  },
});

const onUpdateLineItemDone: OrderingMachineAction = assign({
  selectedTip({ context, event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return getNewTip(
      context.selectedTip,
      context.cart.maxTipAmount,
      context.availableTips,
      event.output?.ledger,
    );
  },
  cart({ context, event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return event.output ?? context.cart;
  },
  appliedRewardId({ event }) {
    assertEvent(event, 'xstate.done.actor.updateLineItem');
    return event.output?.appliedRewardId;
  },
  lineItemIdsBeingUpdated: [],
});

const changeChannelOrLocation: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `changeChannelOrLocation` action.');
};

const requestLogin: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `requestLogin` action.');
};

const onBagReady: OrderingMachineAction = () => {
  orderingMachineLog.debug('Please provide `onBagReady` action.');
};

const changeDeliveryPreferences: OrderingMachineAction = assign({
  cart({ context, event }) {
    assertEvent(event, 'CHANGE_DELIVERY_PREFERENCES');
    return { ...context.cart, deliveryPreferences: event.params };
  },
});

const changeTip: OrderingMachineAction = assign({
  selectedTip({ event }) {
    assertEvent(event, 'CHANGE_TIP');
    return event.params.tip;
  },
});

const changeDropoffLocation: OrderingMachineAction = assign({
  dropoffLocation({ event }) {
    assertEvent(event, 'CHANGE_DROPOFF_LOCATION');
    return event.params.dropoffLocation;
  },
});

const changeCreditUsage: OrderingMachineAction = assign({
  shouldUseCredit({ event }) {
    assertEvent(event, 'CHANGE_CREDIT_USAGE');
    return event.params.shouldUseCredit;
  },
});

const changeUtensilsUsage: OrderingMachineAction = assign({
  shouldIncludeUtensils({ event }) {
    assertEvent(event, 'CHANGE_UTENSILS_USAGE');
    return event.params.shouldIncludeUtensils;
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const OrderingActions = {
  resetContext,
  onFetchInitialDataDone,
  setUpsellBeingAdded,
  setLineItemQuantity,
  onAddUpsellToBagDone,
  onUpdateLineItemDone,
  changeChannelOrLocation,
  requestLogin,
  changeDeliveryPreferences,
  changeTip,
  changeDropoffLocation,
  changeCreditUsage,
  changeUtensilsUsage,
  onBagReady,
  ...OrderingPaymentActions,
  ...OrderingPostOrderActions,
  ...OrderingRewardsActions,
  ...OrderingTimeActions,
};
